import { supabaseService } from '../db/supabase';
import { UIComponents } from '../ui/components';
import type { AppState, MovimentacaoPatrimonial } from '../types';

const byId = <T extends HTMLElement>(id: string) => document.getElementById(id) as T;

export class ModuloMovimentacao {
  constructor(private state: AppState) {}

  inicializar() {
    this.prepararCampos();
    this.vincularEventos();
    void this.carregarLista();
  }

  private prepararCampos() {
    const campusNome = this.state.campus?.nome || this.state.campus?.id || '-';
    const origemCampus = byId<HTMLElement>('mov-origem-campus');
    const destinoCampus = byId<HTMLElement>('mov-destino-campus');
    if (origemCampus) origemCampus.textContent = campusNome;
    if (destinoCampus) destinoCampus.textContent = campusNome;

    const descricao = byId<HTMLInputElement>('mov-descricao');
    if (descricao) descricao.readOnly = true;

    this.popularSelects('origem', {
      sede: this.state.sede || undefined,
      bloco: this.state.bloco || undefined,
      andar: this.state.andar || undefined,
      sala: this.state.sala || undefined,
    });

    this.popularSelects('destino');
  }

  private async carregarLista() {
    const lista = byId('mov-lista');
    if (!lista || !this.state.campus) return;

    lista.textContent = 'Carregando movimentações...';

    try {
      const dados = await supabaseService.listarMovimentacoes(this.state.campus.id, this.state.session?.userId || undefined);
      if (!dados.length) {
        lista.textContent = 'Nenhuma movimentação informada por você neste campus.';
        return;
      }

      lista.innerHTML = dados.map(item => this.renderItem(item)).join('');
      lista.querySelectorAll<HTMLButtonElement>('[data-mov-excluir]').forEach(btn => {
        btn.addEventListener('click', () => this.excluir(btn.dataset.movExcluir || ''));
      });
      lista.querySelectorAll<HTMLButtonElement>('[data-mov-efetivada]').forEach(btn => {
        btn.addEventListener('click', () => {
          UIComponents.info('Informe também a efetivação ao setor patrimonial oficial.');
          btn.disabled = true;
          btn.textContent = 'Efetivação informada';
        });
      });
    } catch (error) {
      lista.textContent = `Erro ao carregar: ${(error as Error).message}`;
    }
  }

  private renderItem(item: MovimentacaoPatrimonial) {
    const origem = [item.origem_sede, item.origem_bloco, item.origem_andar, item.origem_sala].filter(Boolean).join(' / ') || '-';
    const destino = [item.destino_sede, item.destino_bloco, item.destino_andar, item.destino_sala].filter(Boolean).join(' / ') || '-';
    const dataBr = this.formatarDataBr(item.data_movimentacao);
    const acoes: string[] = [];

    if (item.id && (this.state.session?.papel === 'admin' || this.state.session?.papel === 'gestor')) {
      acoes.push(`<button class="btn ghost" type="button" data-mov-excluir="${item.id}">Excluir</button>`);
    }

    if (item.id) {
      acoes.push('<button class="btn ghost" type="button" data-mov-efetivada>Informar efetivada no sistema de patrimônio</button>');
    }

    return `
      <div class="list-item mov-item">
        <div>
          <strong>${item.tombo}</strong>
          <br><small>${item.descricao_bem || 'Sem descrição'}</small>
          <br><small>Registrada em ${dataBr}</small>
          <br><small>Origem: ${origem}</small>
          <br><small>Destino: ${destino}</small>
          ${item.responsavel ? `<br><small>Responsável: ${item.responsavel}</small>` : ''}
        </div>
        <div class="mov-item-actions">${acoes.join('')}</div>
      </div>
    `;
  }

  private async salvar() {
    if (!this.state.campus) return;

    const tombo = byId<HTMLInputElement>('mov-tombo')?.value.trim();
    const descricao = byId<HTMLInputElement>('mov-descricao')?.value.trim();

    if (!tombo) {
      UIComponents.warning('Informe o tombo do bem.');
      return;
    }

    const payload: MovimentacaoPatrimonial = {
      campus: this.state.campus.id,
      tombo,
      descricao_bem: descricao || null,
      tipo: 'ajuste_local',
      data_movimentacao: this.gerarDataISO(new Date()),
      origem_sede: this.getSelectValue('mov-origem-sede'),
      origem_bloco: this.getSelectValue('mov-origem-bloco'),
      origem_andar: this.getSelectValue('mov-origem-andar'),
      origem_sala: this.getSelectValue('mov-origem-sala'),
      destino_campus: this.state.campus.id,
      destino_sede: this.getSelectValue('mov-destino-sede'),
      destino_bloco: this.getSelectValue('mov-destino-bloco'),
      destino_andar: this.getSelectValue('mov-destino-andar'),
      destino_sala: this.getSelectValue('mov-destino-sala'),
      responsavel: byId<HTMLInputElement>('mov-responsavel')?.value.trim() || null,
      processo_sei: null,
      observacao: byId<HTMLTextAreaElement>('mov-observacao')?.value.trim() || null,
      criado_por: this.state.session?.userId || null,
    };

    const loading = UIComponents.showLoading('Salvando movimentação...');
    try {
      await supabaseService.inserirMovimentacao(payload);
      UIComponents.hideLoading(loading);
      UIComponents.success('Movimentação salva com sucesso!');
      this.limparFormulario();
      await this.carregarLista();
    } catch (error) {
      UIComponents.hideLoading(loading);
      UIComponents.error((error as Error).message);
    }
  }

  private limparFormulario() {
    ['mov-tombo', 'mov-descricao', 'mov-responsavel'].forEach(id => {
      const input = byId<HTMLInputElement>(id);
      if (input) input.value = '';
    });
    const obs = byId<HTMLTextAreaElement>('mov-observacao');
    if (obs) obs.value = '';
    this.popularSelects('destino');
  }

  private async buscarTombo() {
    const input = byId<HTMLInputElement>('mov-tombo');
    const descricao = byId<HTMLInputElement>('mov-descricao');
    if (!input || !descricao) return;
    const tombo = input.value.trim();
    if (!tombo) {
      UIComponents.warning('Informe o tombo do bem para buscar.');
      return;
    }

    const loading = UIComponents.showLoading('Consultando bem...');
    try {
      const bem = await supabaseService.buscarBemPorTombo(tombo);
      UIComponents.hideLoading(loading);
      if (!bem) {
        descricao.value = '';
        UIComponents.warning('Bem não localizado na base patrimonial.');
        return;
      }
      descricao.value = bem.descricao || '';
      UIComponents.success('Bem localizado e descrição preenchida.');
    } catch (error) {
      UIComponents.hideLoading(loading);
      UIComponents.error((error as Error).message);
    }
  }

  private async excluir(id: string) {
    if (!id) return;
    const confirma = await UIComponents.confirm('Deseja excluir esta movimentação?', 'Excluir movimentação');
    if (!confirma) return;

    try {
      await supabaseService.excluirMovimentacao(id);
      UIComponents.success('Movimentação excluída.');
      await this.carregarLista();
    } catch (error) {
      UIComponents.error((error as Error).message);
    }
  }

  private vincularEventos() {
    byId('btn-salvar-movimentacao')?.addEventListener('click', () => this.salvar());
    byId('btn-limpar-movimentacao')?.addEventListener('click', () => this.limparFormulario());
    byId('mov-buscar-tombo')?.addEventListener('click', () => this.buscarTombo());

    ['origem', 'destino'].forEach(prefix => {
      byId<HTMLSelectElement>(`mov-${prefix}-sede`)?.addEventListener('change', () => this.onSedeChange(prefix as 'origem' | 'destino'));
      byId<HTMLSelectElement>(`mov-${prefix}-bloco`)?.addEventListener('change', () => this.onBlocoChange(prefix as 'origem' | 'destino'));
      byId<HTMLSelectElement>(`mov-${prefix}-andar`)?.addEventListener('change', () => this.onAndarChange(prefix as 'origem' | 'destino'));
    });
  }

  private onSedeChange(prefix: 'origem' | 'destino') {
    this.popularBlocos(prefix);
    this.popularAndares(prefix);
    this.popularSalas(prefix);
  }

  private onBlocoChange(prefix: 'origem' | 'destino') {
    this.popularAndares(prefix);
    this.popularSalas(prefix);
  }

  private onAndarChange(prefix: 'origem' | 'destino') {
    this.popularSalas(prefix);
  }

  private popularSelects(prefix: 'origem' | 'destino', defaults: Partial<Record<'sede' | 'bloco' | 'andar' | 'sala', string>> = {}) {
    this.popularSedes(prefix, defaults.sede);
    this.popularBlocos(prefix, defaults.bloco);
    this.popularAndares(prefix, defaults.andar);
    this.popularSalas(prefix, defaults.sala);
  }

  private popularSedes(prefix: 'origem' | 'destino', selected?: string) {
    const select = byId<HTMLSelectElement>(`mov-${prefix}-sede`);
    if (!select) return;
    const sedes = this.uniqueValues(this.state.locaisDb.map(l => l.sede));
    this.preencherSelect(select, sedes, selected);
  }

  private popularBlocos(prefix: 'origem' | 'destino', selected?: string) {
    const sede = this.getSelectValue(`mov-${prefix}-sede`);
    const select = byId<HTMLSelectElement>(`mov-${prefix}-bloco`);
    if (!select) return;
    const blocos = sede ? this.uniqueValues(this.state.locaisDb.filter(l => l.sede === sede).map(l => l.bloco)) : [];
    this.preencherSelect(select, blocos, selected);
  }

  private popularAndares(prefix: 'origem' | 'destino', selected?: string) {
    const sede = this.getSelectValue(`mov-${prefix}-sede`);
    const bloco = this.getSelectValue(`mov-${prefix}-bloco`);
    const select = byId<HTMLSelectElement>(`mov-${prefix}-andar`);
    if (!select) return;
    const andares = sede && bloco
      ? this.uniqueValues(this.state.locaisDb.filter(l => l.sede === sede && l.bloco === bloco).map(l => l.andar))
      : [];
    this.preencherSelect(select, andares, selected);
  }

  private popularSalas(prefix: 'origem' | 'destino', selected?: string) {
    const sede = this.getSelectValue(`mov-${prefix}-sede`);
    const bloco = this.getSelectValue(`mov-${prefix}-bloco`);
    const andar = this.getSelectValue(`mov-${prefix}-andar`);
    const select = byId<HTMLSelectElement>(`mov-${prefix}-sala`);
    if (!select) return;
    const salas = sede && bloco && andar
      ? this.uniqueValues(this.state.locaisDb.filter(l => l.sede === sede && l.bloco === bloco && l.andar === andar).map(l => l.sala))
      : [];
    this.preencherSelect(select, salas, selected);
  }

  private preencherSelect(select: HTMLSelectElement, values: string[], selected?: string) {
    select.innerHTML = '';
    const uniqueValues = values.filter(Boolean);
    const temOpcoes = uniqueValues.length > 0;
    const wrapper = select.closest('.select-field');
    let staticValue = wrapper?.querySelector('.select-static-value') as HTMLSpanElement | null;

    if (!staticValue && wrapper) {
      staticValue = document.createElement('span');
      staticValue.className = 'select-static-value';
      wrapper.appendChild(staticValue);
    }

    if (uniqueValues.length !== 1) {
      const placeholder = document.createElement('option');
      placeholder.value = '';
      placeholder.textContent = temOpcoes ? 'Selecione' : 'Sem opções';
      select.appendChild(placeholder);
    }

    uniqueValues.forEach(valor => {
      const option = document.createElement('option');
      option.value = valor;
      option.textContent = this.formatarLabel(valor);
      select.appendChild(option);
    });

    if (selected && uniqueValues.includes(selected)) {
      select.value = selected;
    } else if (uniqueValues.length === 1) {
      select.value = uniqueValues[0];
      select.disabled = true;
      select.classList.add('select-hidden');
      wrapper?.classList.add('field-static');
      if (staticValue) {
        staticValue.textContent = this.formatarLabel(uniqueValues[0]);
        staticValue.classList.add('visible');
      }
      return;
    } else {
      select.value = '';
    }

    select.disabled = !temOpcoes;
    select.classList.remove('select-hidden');
    wrapper?.classList.remove('field-static');
    if (staticValue) {
      staticValue.textContent = '';
      staticValue.classList.remove('visible');
    }
    if (!temOpcoes) {
      wrapper?.classList.remove('field-static');
    }
  }

  private uniqueValues(values: (string | null | undefined)[]): string[] {
    return [...new Set(values.filter(Boolean) as string[])].sort((a, b) => a.localeCompare(b, 'pt-BR', { sensitivity: 'base' }));
  }

  private formatarLabel(value: string): string {
    if (!value) return '-';
    const normalizado = value.length <= 3 ? value.toUpperCase() : value.replace(/\b\w/g, l => l.toUpperCase());
    return normalizado.replace('ÚNica', 'Única');
  }

  private getSelectValue(id: string): string | null {
    const select = byId<HTMLSelectElement>(id);
    if (!select) return null;
    return select.value || null;
  }

  private gerarDataISO(date: Date): string {
    const ano = date.getFullYear();
    const mes = String(date.getMonth() + 1).padStart(2, '0');
    const dia = String(date.getDate()).padStart(2, '0');
    return `${ano}-${mes}-${dia}`;
  }

  private formatarDataBr(data?: string | null): string {
    if (!data) return '-';
    const d = new Date(data);
    if (Number.isNaN(d.getTime())) {
      const partes = data.split('-');
      if (partes.length === 3) return `${partes[2]}/${partes[1]}/${partes[0]}`;
      return data;
    }
    const dia = String(d.getDate()).padStart(2, '0');
    const mes = String(d.getMonth() + 1).padStart(2, '0');
    const ano = d.getFullYear();
    return `${dia}/${mes}/${ano}`;
  }
}
